import React, { useState } from "react"
import PropTypes from "prop-types"
import logo from "../images/img_logo.svg"
import Link from "./Link"
import { Button } from "./Button"
import useHover from "../data/useHover"
import { useTopics } from "../data/useTopics"
import { useTeams } from "../data/useTeams"
import { useSetting } from "../data/useSetting"
import { Disclosure, Transition } from "@headlessui/react"
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/20/solid"
import SearchBar from "./SearchBar"
import MagnifyingGlass from "../images/magnifying-glass.svg"

export function Logo() {
  return (
    <Link to="/" className="flex-shrink-0">
      <img src={logo} alt="mental hug logo" width={146} height={36} />
    </Link>
  )
}

export default function Navbar() {
  const [hoverRef, isHovered] = useHover()
  const [isOpen, setIsOpen] = useState(false)
  const [hoverTeams, isHoveredTeams] = useHover()
  const [isTeamsOpen, setIsTeamsOpen] = useState(false)
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false)

  const { topics } = useTopics()
  const { teams } = useTeams()
  const {
    contentfulSetting: { lineLink, reserveLink },
  } = useSetting()

  const Topics = dots => {
    return topics.nodes.map(topic => (
      <Link
        key={topic.id}
        to={`/${topic.slug}`}
        className="text-gray-500 hover:text-gray-900 px-3"
      >
        {dots ? (
          <p className="leading-normal tracking-wide">- {topic.name}</p>
        ) : (
          <p>{topic.name}</p>
        )}
      </Link>
    ))
  }

  Topics.propTypes = {
    dots: PropTypes.bool,
  }

  const Teams = dots => {
    return teams.nodes.map(team => (
      <Link
        key={team.id}
        to={`/teams/${team.slug}`}
        className="text-gray-500 hover:text-gray-900 px-3"
      >
        {dots ? (
          <p className="leading-normal tracking-wide">- {team.title}</p>
        ) : (
          <p>{team.title}</p>
        )}
      </Link>
    ))
  }

  Teams.propTypes = {
    dots: PropTypes.bool,
  }

  return (
    <>
      <div
        className={`fixed duration-300 z-20 ease-in-out ${
          isSearchBarOpen
            ? "opacity-100 inset-0 bg-black bg-opacity-50"
            : "opacity-0 pointer-events-none"
        }`}
      />
      <Disclosure
        as="nav"
        className="fixed w-full bg-white h-16 z-30 border-b border-gray-300"
      >
        {({ open }) => (
          <>
            {isSearchBarOpen && (
              <div className="wrapper h-16 flex z-30 items-center">
                <SearchBar
                  isSearchBarOpen={isSearchBarOpen}
                  setIsSearchBarOpen={setIsSearchBarOpen}
                />
              </div>
            )}
            <div className={`wrapper ${isSearchBarOpen ? "hidden" : ""}`}>
              <div className="flex justify-between h-16">
                <div className="flex xl:gap-16">
                  {/* LOGO */}
                  <div className="flex-shrink-0 flex items-center">
                    <Logo />
                  </div>
                  <div className="hidden lg:gap-4 xl:flex 2xl:gap-8 lg:text-xs 2xl:text-base">
                    <div className="inline-flex items-center text-gray-500 hover:text-gray-900">
                      <Link to="/about">關於我們</Link>
                    </div>
                    <div
                      ref={hoverTeams}
                      className="relative block items-center text-gray-500 hover:text-gray-900"
                    >
                      <div className="flex items-center h-full">
                        <p>專業團隊</p>
                        <ChevronDownIcon className="ml-1 h-4 w-4 text-red-300" />
                      </div>

                      <div
                        className={`absolute w-32 top-16 -left-1/3 transition-all duration-300 ease-in-out -mt-0.5 ${
                          isHoveredTeams
                            ? "h-48 z-10 border-t-2 border-red-300"
                            : "h-0 -z-50 border-t-2 border-transparent"
                        }`}
                      >
                        <div
                          className={`${
                            isHoveredTeams ? "grid" : "hidden"
                          } transition-all duration-300 ease-in-out bg-white border-x border-b border-gray-300 space-y-5 py-6 text-center overflow-y-auto`}
                        >
                          {Teams(false)}
                        </div>
                      </div>
                    </div>
                    <div className="inline-flex items-center text-gray-500 hover:text-gray-900">
                      <Link to="/consultation">諮商主題</Link>
                    </div>
                    <div className="inline-flex items-center text-gray-500 hover:text-gray-900">
                      <Link to="/psychologist">找心理師</Link>
                    </div>
                    <div className="inline-flex items-center text-gray-500 hover:text-gray-900">
                      <Link to="/service">諮商服務</Link>
                    </div>
                    <div
                      ref={hoverRef}
                      className="relative block items-center text-gray-500 hover:text-gray-900"
                    >
                      <Link to="/blog">
                        <div className="flex items-center h-full">
                          <p>部落格</p>
                          <ChevronDownIcon className="ml-1 h-4 w-4 text-red-300" />
                        </div>
                      </Link>
                      <div
                        className={`absolute w-32 top-16 -left-1/2 transition-all duration-300 ease-in-out -mt-0.5 ${
                          isHovered
                            ? "h-48 z-10 border-t-2 border-red-300"
                            : "h-0 -z-50 border-t-2 border-transparent"
                        }`}
                      >
                        <div
                          className={`${
                            isHovered ? "grid" : "hidden"
                          } transition-all duration-300 ease-in-out bg-white border-x border-b border-gray-300 space-y-5 py-6 text-center overflow-y-auto`}
                        >
                          {Topics(false)}
                        </div>
                      </div>
                    </div>
                    <div className="inline-flex items-center text-gray-500 hover:text-gray-900">
                      <Link to="/english">Languages</Link>
                    </div>
                  </div>
                </div>
                {/* CTA按鈕 */}
                <div className="hidden xl:flex items-center lg:space-x-4">
                  <SearchBar
                    isSearchBarOpen={isSearchBarOpen}
                    setIsSearchBarOpen={setIsSearchBarOpen}
                  />
                  <Link as="a" to={reserveLink}>
                    <Button
                      className="convert convert-line convert-fix"
                      size="sm"
                    >
                      桃園門診預約
                    </Button>
                  </Link>
                  <Link as="a" to={lineLink}>
                    <Button
                      className="convert convert-line convert-fix"
                      size="sm"
                    >
                      立即預約
                    </Button>
                  </Link>
                </div>
                <div className="-mr-2 flex items-center xl:hidden">
                  {/* 手機平板搜尋鈕 */}
                  <button
                    className="xl:hidden"
                    onClick={() => {
                      setTimeout(() => {
                        setIsSearchBarOpen(prev => !prev)
                      }, 0)
                    }}
                  >
                    <img
                      src={MagnifyingGlass}
                      alt="magnifying-glass"
                      className="w-6 h-6 mr-4"
                    />
                  </button>
                  {/* Mobile menu button */}
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            {/* Mobile Menu content */}
            <Transition
              show={open}
              enter="transition duration-300 ease-in-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition duration-300 ease-in-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Disclosure.Panel className="bg-black bg-opacity-50 z-10 h-screen xl:hidden">
                <div className="bg-white z-30 min-h-md drop-shadow-lg px-6 divide-y divide-bg-900">
                  <div className="container mx-auto sm:px-8 py-6 text-xl tracking-wide text-gray-700">
                    <Link to="/about">關於我們</Link>
                  </div>
                  <div className="container mx-auto sm:px-8 py-6 text-xl tracking-wide text-gray-700 flex justify-between">
                    <div className="w-1/2">
                      <div>專業團隊</div>
                    </div>
                    <div
                      className="w-1/2 inline-flex justify-end my-auto text-red-300"
                      onClick={() => setIsTeamsOpen(prev => !prev)}
                    >
                      {isTeamsOpen ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                  {isTeamsOpen && (
                    <div className="container mx-auto sm:px-8 border-t border-transparent pb-6">
                      <div className="flex flex-col space-y-4">
                        {Teams(true)}
                      </div>
                    </div>
                  )}

                  <div className="container mx-auto sm:px-8 py-6 text-xl tracking-wide text-gray-700">
                    <Link to="/consultation">諮商主題</Link>
                  </div>
                  <div className="container mx-auto sm:px-8 py-6 text-xl tracking-wide text-gray-700">
                    <Link to="/psychologist">找心理師</Link>
                  </div>
                  <div className="container mx-auto sm:px-8 py-6 text-xl tracking-wide text-gray-700">
                    <Link to="/service">諮商服務</Link>
                  </div>

                  <div className="container mx-auto sm:px-8 py-6 text-xl tracking-wide text-gray-700 flex justify-between">
                    <div className="w-1/2">
                      <Link to="/blog">部落格</Link>
                    </div>
                    <div
                      className="w-1/2 inline-flex justify-end my-auto text-red-300"
                      onClick={() => setIsOpen(prev => !prev)}
                    >
                      {isOpen ? (
                        <ChevronUpIcon className="h-4 w-4" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="container mx-auto sm:px-8 border-t border-transparent pb-6">
                      <div className="flex flex-col space-y-4">
                        {Topics(true)}
                      </div>
                    </div>
                  )}

                  <div className="container mx-auto sm:px-8 py-8 text-xl tracking-wide text-gray-700">
                    <Link to="/english">Languages</Link>
                  </div>
                </div>
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>
    </>
  )
}
